import toast from 'react-hot-toast'

export interface AppError {
  code: string
  message: string
  userMessage: string
  details?: Record<string, any>
  errorId?: string
  timestamp?: string
}

export interface ErrorResponse {
  error: AppError
}

export class ErrorHandler {
  private static readonly errorMessages: Record<string, string> = {
    // Network errors
    NETWORK_ERROR: 'Keine Internetverbindung. Bitte überprüfen Sie Ihre Verbindung.',
    TIMEOUT_ERROR: 'Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.',
    
    // Authentication errors
    AUTH_REQUIRED: 'Bitte melden Sie sich an, um fortzufahren.',
    AUTH_EXPIRED: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
    INVALID_CREDENTIALS: 'Ungültige Anmeldedaten. Bitte überprüfen Sie Ihre E-Mail und Ihr Passwort.',
    
    // Authorization errors
    PERMISSION_DENIED: 'Sie haben keine Berechtigung für diese Aktion.',
    ROLE_REQUIRED: 'Diese Funktion erfordert erweiterte Berechtigungen.',
    
    // Validation errors
    VALIDATION_ERROR: 'Bitte überprüfen Sie Ihre Eingaben.',
    REQUIRED_FIELD: 'Dieses Feld ist erforderlich.',
    INVALID_FORMAT: 'Ungültiges Format. Bitte überprüfen Sie Ihre Eingabe.',
    FILE_TOO_LARGE: 'Die Datei ist zu groß. Maximale Größe: 50 MB.',
    UNSUPPORTED_FILE_TYPE: 'Dieser Dateityp wird nicht unterstützt.',
    
    // Search errors
    SEARCH_FAILED: 'Die Suche konnte nicht durchgeführt werden.',
    NO_RESULTS: 'Keine Ergebnisse gefunden. Versuchen Sie andere Suchbegriffe.',
    
    // Upload errors
    UPLOAD_FAILED: 'Der Upload ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    PROCESSING_FAILED: 'Die Datei konnte nicht verarbeitet werden.',
    
    // Rate limiting
    RATE_LIMIT: 'Zu viele Anfragen. Bitte warten Sie einen Moment.',
    
    // Server errors
    SERVER_ERROR: 'Ein Serverfehler ist aufgetreten. Unser Team wurde benachrichtigt.',
    SERVICE_UNAVAILABLE: 'Der Service ist momentan nicht verfügbar. Bitte versuchen Sie es später erneut.',
    
    // Default
    UNKNOWN_ERROR: 'Ein unerwarteter Fehler ist aufgetreten.',
  }

  /**
   * Handle API error responses
   */
  static handleApiError(error: any): void {
    // Check if it's a network error
    if (!navigator.onLine) {
      this.showError('NETWORK_ERROR')
      return
    }

    // Handle timeout
    if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
      this.showError('TIMEOUT_ERROR')
      return
    }

    // Handle API error response
    if (error.response?.data?.error) {
      const apiError = error.response.data.error as AppError
      this.showErrorMessage(apiError.userMessage || apiError.message, apiError.code)
      
      // Log error details for debugging
      console.error('API Error:', {
        errorId: apiError.errorId,
        code: apiError.code,
        details: apiError.details,
        timestamp: apiError.timestamp
      })
      
      // Handle specific error codes
      this.handleSpecificError(apiError.code, apiError)
      return
    }

    // Handle HTTP status codes
    if (error.response?.status) {
      this.handleHttpError(error.response.status, error.response.data)
      return
    }

    // Default error
    this.showError('UNKNOWN_ERROR')
    console.error('Unhandled error:', error)
  }

  /**
   * Handle specific error codes
   */
  private static handleSpecificError(code: string, error: AppError): void {
    switch (code) {
      case 'AUTHENTICATION_ERROR':
      case 'AUTH_EXPIRED':
        // Redirect to login
        window.location.href = '/login'
        break
      
      case 'RATE_LIMIT_EXCEEDED':
        const retryAfter = error.details?.retry_after || 60
        this.showErrorMessage(
          `Zu viele Anfragen. Bitte warten Sie ${retryAfter} Sekunden.`,
          'RATE_LIMIT',
          retryAfter * 1000
        )
        break
      
      case 'FILE_PROCESSING_ERROR':
        const filename = error.details?.filename || 'Datei'
        const reason = error.details?.reason || 'Unbekannter Fehler'
        this.showErrorMessage(
          `${filename} konnte nicht verarbeitet werden: ${reason}`,
          'PROCESSING_FAILED'
        )
        break
    }
  }

  /**
   * Handle HTTP status codes
   */
  private static handleHttpError(status: number, data?: any): void {
    switch (status) {
      case 401:
        this.showError('AUTH_REQUIRED')
        window.location.href = '/login'
        break
      case 403:
        this.showError('PERMISSION_DENIED')
        break
      case 404:
        this.showErrorMessage('Die angeforderte Ressource wurde nicht gefunden.')
        break
      case 429:
        this.showError('RATE_LIMIT')
        break
      case 500:
      case 502:
      case 503:
        this.showError('SERVER_ERROR')
        break
      default:
        this.showError('UNKNOWN_ERROR')
    }
  }

  /**
   * Show error message
   */
  static showError(code: string | any): void {
    if (typeof code === 'object' && code.message) {
      this.showErrorMessage(code.message)
      return
    }
    const message = this.errorMessages[code] || this.errorMessages.UNKNOWN_ERROR
    this.showErrorMessage(message, code)
  }

  /**
   * Show error toast
   */
  static showErrorMessage(
    message: string,
    code?: string,
    duration?: number
  ): void {
    toast.error(message, {
      duration: duration || 5000,
      id: code, // Prevent duplicate toasts
      style: {
        background: '#dc2626',
        color: '#fff',
        borderRadius: '8px',
        padding: '16px',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#dc2626',
      },
    })
  }

  /**
   * Show success message
   */
  static showSuccess(message: string): void {
    toast.success(message, {
      duration: 3000,
      style: {
        background: '#059669',
        color: '#fff',
        borderRadius: '8px',
        padding: '16px',
      },
      iconTheme: {
        primary: '#fff',
        secondary: '#059669',
      },
    })
  }

  /**
   * Show warning message
   */
  static showWarning(message: string): void {
    toast(message, {
      duration: 4000,
      icon: '⚠️',
      style: {
        background: '#f59e0b',
        color: '#fff',
        borderRadius: '8px',
        padding: '16px',
      },
    })
  }

  /**
   * Show info message
   */
  static showInfo(message: string): void {
    toast(message, {
      duration: 3000,
      icon: 'ℹ️',
      style: {
        background: '#3b82f6',
        color: '#fff',
        borderRadius: '8px',
        padding: '16px',
      },
    })
  }

  /**
   * Validate form field
   */
  static validateField(
    value: any,
    rules: ValidationRule[]
  ): string | null {
    for (const rule of rules) {
      const error = rule(value)
      if (error) return error
    }
    return null
  }
}

// Validation rules
export type ValidationRule = (value: any) => string | null

export const ValidationRules = {
  required: (message = 'Dieses Feld ist erforderlich'): ValidationRule =>
    (value) => (!value || value.length === 0 ? message : null),

  minLength: (min: number, message?: string): ValidationRule =>
    (value) => (value && value.length < min 
      ? message || `Mindestens ${min} Zeichen erforderlich` 
      : null),

  maxLength: (max: number, message?: string): ValidationRule =>
    (value) => (value && value.length > max 
      ? message || `Maximal ${max} Zeichen erlaubt` 
      : null),

  email: (message = 'Ungültige E-Mail-Adresse'): ValidationRule =>
    (value) => (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) 
      ? message 
      : null),

  pattern: (pattern: RegExp, message: string): ValidationRule =>
    (value) => (value && !pattern.test(value) ? message : null),
}

// Error boundary component helper
export function createErrorFallback(error: Error, resetError: () => void) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="glass max-w-md w-full p-8 text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold text-white mb-4">
          Ein Fehler ist aufgetreten
        </h2>
        <p className="text-gray-300 mb-6">
          Die Anwendung konnte nicht geladen werden. Bitte versuchen Sie es erneut.
        </p>
        <div className="space-y-4">
          <button
            onClick={resetError}
            className="w-full btn-primary"
          >
            Erneut versuchen
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full btn-secondary"
          >
            Zur Startseite
          </button>
        </div>
        {process.env.NODE_ENV === 'development' && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-gray-400">
              Fehlerdetails (nur Entwicklung)
            </summary>
            <pre className="mt-2 text-xs text-gray-500 overflow-auto">
              {error.message}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}